import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Mail, Phone, Shield, ArrowRight, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const VerificationPage = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser, API } = useAuth();
  const [emailCode, setEmailCode] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [loading, setLoading] = useState({ email: false, phone: false, terms: false, resendEmail: false, resendPhone: false });
  const [resendEmailCooldown, setResendEmailCooldown] = useState(0);
  const [resendPhoneCooldown, setResendPhoneCooldown] = useState(0);
  const [emailSendCount, setEmailSendCount] = useState(1);
  const [phoneSendCount, setPhoneSendCount] = useState(1);

  // Redirect if at least one verification done + terms accepted
  useEffect(() => {
    if ((user?.email_verified || user?.phone_verified) && user?.terms_accepted) {
      navigate('/listings');
    }
  }, [user, navigate]);

  // Cooldown timer for email resend button
  useEffect(() => {
    if (resendEmailCooldown > 0) {
      const timer = setTimeout(() => setResendEmailCooldown(resendEmailCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendEmailCooldown]);

  // Cooldown timer for phone resend button
  useEffect(() => {
    if (resendPhoneCooldown > 0) {
      const timer = setTimeout(() => setResendPhoneCooldown(resendPhoneCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendPhoneCooldown]);

  const formatCooldown = (seconds) => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${mins}m`;
    } else if (seconds >= 60) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}m ${secs}s`;
    }
    return `${seconds}s`;
  };

  const verifyEmail = async () => {
    setLoading({ ...loading, email: true });
    try {
      await axios.post(`${API}/auth/verify-email`, { code: emailCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email vérifié !');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setLoading({ ...loading, email: false });
    }
  };

  const resendEmailCode = async () => {
    setLoading({ ...loading, resendEmail: true });
    try {
      const response = await axios.post(`${API}/auth/resend-email-code`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.email_sent) {
        toast.success(response.data.message);
      } else {
        toast.info(`Code: ${response.data.code}`, {
          duration: 10000,
          description: response.data.note || 'Email non envoyé - code affiché pour test'
        });
      }
      
      setEmailSendCount(response.data.send_count);
      if (response.data.next_delay_seconds) {
        setResendEmailCooldown(response.data.next_delay_seconds);
      }
      
      if (response.data.remaining_sends === 0) {
        toast.warning('Dernier envoi autorisé. Contactez le support si besoin.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du renvoi');
    } finally {
      setLoading({ ...loading, resendEmail: false });
    }
  };

  const verifyPhone = async () => {
    setLoading({ ...loading, phone: true });
    try {
      await axios.post(`${API}/auth/verify-phone`, { code: phoneCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Téléphone vérifié !');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setLoading({ ...loading, phone: false });
    }
  };

  const resendPhoneCode = async () => {
    setLoading({ ...loading, resendPhone: true });
    try {
      const response = await axios.post(`${API}/auth/resend-phone-code`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.sms_sent) {
        toast.success(response.data.message);
      } else {
        toast.info(`Code: ${response.data.code}`, {
          duration: 10000,
          description: response.data.note || 'SMS non envoyé - code affiché pour test'
        });
      }
      
      setPhoneSendCount(response.data.send_count);
      if (response.data.next_delay_seconds) {
        setResendPhoneCooldown(response.data.next_delay_seconds);
      }
      
      if (response.data.remaining_sends === 0) {
        toast.warning('Dernier envoi autorisé. Contactez le support si besoin.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du renvoi');
    } finally {
      setLoading({ ...loading, resendPhone: false });
    }
  };

  const acceptTerms = async () => {
    setLoading({ ...loading, terms: true });
    try {
      await axios.post(`${API}/auth/accept-terms`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Conditions acceptées !');
      await refreshUser();
      navigate('/create-listing');
    } catch (error) {
      toast.error('Erreur lors de l\'acceptation');
    } finally {
      setLoading({ ...loading, terms: false });
    }
  };

  const allVerified = user?.email_verified || user?.phone_verified;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 py-24">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Vérification du compte</h1>
          <p className="text-muted-foreground">
            Complétez les étapes de vérification pour activer votre compte
          </p>
        </div>

        <div className="space-y-6">
          {/* Email Verification */}
          <Card className={`card-dashboard ${user?.email_verified ? 'border-green-500/50' : ''}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    user?.email_verified ? 'bg-green-500/20' : 'bg-primary/10'
                  }`}>
                    <Mail className={`w-5 h-5 ${user?.email_verified ? 'text-green-500' : 'text-primary'}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Vérification Email</CardTitle>
                    <CardDescription>{user?.email}</CardDescription>
                  </div>
                </div>
                {user?.email_verified && (
                  <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Vérifié
                  </Badge>
                )}
              </div>
            </CardHeader>
            {!user?.email_verified && (
              <CardContent>
                <div className="flex gap-3">
                  <Input
                    placeholder="Entrez le code reçu par email"
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.toUpperCase())}
                    className="h-12"
                    data-testid="verify-email-input"
                  />
                  <Button 
                    onClick={verifyEmail} 
                    disabled={loading.email || !emailCode}
                    data-testid="verify-email-btn"
                  >
                    {loading.email ? 'Vérification...' : 'Vérifier'}
                  </Button>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-xs text-muted-foreground">
                    Vérifiez votre boîte de réception et vos spams
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resendEmailCode}
                    disabled={loading.resendEmail || resendEmailCooldown > 0 || emailSendCount >= 5}
                    className="text-primary"
                    data-testid="resend-email-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading.resendEmail ? 'animate-spin' : ''}`} />
                    {emailSendCount >= 5 ? 'Limite atteinte' : 
                     resendEmailCooldown > 0 ? `Attendre ${formatCooldown(resendEmailCooldown)}` : 'Renvoyer le code'}
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Phone Verification */}
          <Card className={`card-dashboard ${user?.phone_verified ? 'border-green-500/50' : ''}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    user?.phone_verified ? 'bg-green-500/20' : 'bg-primary/10'
                  }`}>
                    <Phone className={`w-5 h-5 ${user?.phone_verified ? 'text-green-500' : 'text-primary'}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Vérification Téléphone</CardTitle>
                    <CardDescription>{user?.phone}</CardDescription>
                  </div>
                </div>
                {user?.phone_verified && (
                  <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Vérifié
                  </Badge>
                )}
              </div>
            </CardHeader>
            {!user?.phone_verified && (
              <CardContent>
                <div className="flex gap-3">
                  <Input
                    placeholder="Entrez le code reçu par SMS"
                    value={phoneCode}
                    onChange={(e) => setPhoneCode(e.target.value.toUpperCase())}
                    className="h-12"
                    data-testid="verify-phone-input"
                  />
                  <Button 
                    onClick={verifyPhone} 
                    disabled={loading.phone || !phoneCode}
                    data-testid="verify-phone-btn"
                  >
                    {loading.phone ? 'Vérification...' : 'Vérifier'}
                  </Button>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-xs text-muted-foreground">
                    Un code a été envoyé à votre numéro
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resendPhoneCode}
                    disabled={loading.resendPhone || resendPhoneCooldown > 0 || phoneSendCount >= 5}
                    className="text-primary"
                    data-testid="resend-phone-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading.resendPhone ? 'animate-spin' : ''}`} />
                    {phoneSendCount >= 5 ? 'Limite atteinte' : 
                     resendPhoneCooldown > 0 ? `Attendre ${formatCooldown(resendPhoneCooldown)}` : 'Renvoyer le code'}
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Continue Button */}
          {allVerified && (
            <Card className="card-dashboard bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/30">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold">
                      Vérification complétée !
                    </h3>
                    <p className="text-muted-foreground">
                      {user?.email_verified && user?.phone_verified 
                        ? 'Email et téléphone vérifiés. Acceptez les conditions pour continuer.'
                        : user?.email_verified 
                          ? 'Email vérifié. Vous pouvez continuer ou aussi vérifier votre téléphone.'
                          : 'Téléphone vérifié. Vous pouvez continuer ou aussi vérifier votre email.'}
                    </p>
                  </div>
                  <Button 
                    className="btn-primary"
                    onClick={acceptTerms}
                    disabled={loading.terms}
                    data-testid="accept-terms-btn"
                  >
                    {loading.terms ? 'Chargement...' : 'Accepter et continuer'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationPage;
